import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Cpu } from "lucide-react";

/**
 * MobileHero — lightweight static hero shown to mobile visitors instead of the
 * 8MB Panther GLB scroll banner. Preserves the same brand voice and CTAs while
 * cutting page-load bandwidth ~95% on phones.
 */
export default function MobileHero() {
  return (
    <section
      data-testid="mobile-hero"
      className="relative overflow-hidden bg-slate-950 text-white px-6 pt-12 pb-14"
    >
      {/* subtle grid + glow — CSS only, zero JS */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.08) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div
        aria-hidden
        className="absolute -top-24 -right-24 h-72 w-72 rounded-full blur-3xl opacity-40"
        style={{ background: "radial-gradient(closest-side, #3b82f6, transparent)" }}
      />

      <div className="relative">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.25em] text-blue-300 border border-blue-400/30 rounded-full px-3 py-1 mb-6">
          <span className="h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse" />
          Global · Jewellery CAD Marketplace
        </div>

        <h1 className="font-display font-extrabold text-4xl sm:text-5xl leading-[1.05] tracking-tight">
          The world's marketplace for{" "}
          <span className="text-blue-400">jewellery CAD files</span>
        </h1>
        <p className="mt-4 text-sm text-slate-300 leading-relaxed max-w-md">
          3DM &amp; STL files, production-ready. Verified designers across the
          globe, live technical preview, and escrow-protected payments.
        </p>

        <div className="mt-7 flex flex-col sm:flex-row gap-3">
          <Link
            to="/marketplace"
            data-testid="mobile-hero-browse"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-5 py-3 font-semibold text-sm hover:bg-primary/90 transition-colors"
          >
            Browse marketplace <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/register"
            data-testid="mobile-hero-sell"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-700 px-5 py-3 font-semibold text-sm hover:border-blue-400 hover:text-blue-300 transition-colors"
          >
            Start selling
          </Link>
        </div>

        <div className="mt-9 grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-slate-800 bg-slate-900/60 backdrop-blur-sm p-3">
            <ShieldCheck className="h-4 w-4 text-blue-400 mb-1.5" />
            <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Escrow</p>
            <p className="text-xs text-slate-200 mt-0.5">48-hour hold</p>
          </div>
          <div className="rounded-lg border border-slate-800 bg-slate-900/60 backdrop-blur-sm p-3">
            <Cpu className="h-4 w-4 text-blue-400 mb-1.5" />
            <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Preview</p>
            <p className="text-xs text-slate-200 mt-0.5">Live 3D on tap</p>
          </div>
        </div>
      </div>
    </section>
  );
}
